import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAccount, usePartnerAccess } from '../contexts/AccountContext';
import CredentialsTab from './CredentialsTab';
import UapiAccessTab from './UapiAccessTab';
import HotelTemplates from './HotelTemplates';
import OhipTransactionsTab from './OhipTransactionsTab';
import AppsPage from './AppsPage';
import SyncPage from './SyncPage';
import IntegrateSystemsPage from './IntegrateSystemsPage';
import BillingTab, { BillingCreateSection } from './BillingTab';
import HotelPrices from './HotelPrices';
import hotelService from '../services/hotelService';
import vaultService from '../services/vaultService';
import vaultsourceService from '../services/vaultsourceService';
import accountService from '../services/accountService';
import partnerService from '../services/partnerService';
import pricingService from '../services/pricingService';
import productsService from '../services/productsService';
import CountrySelect from './CountrySelect';
import './HotelForm.css';
import './shared/Tabs.css';
import CurrencySelect from './CurrencySelect';
import { SubTabs } from './shared/Tabs';
import SupportTicketsPanel from './support/SupportTicketsPanel';
import SupportButton from './support/SupportButton';
import SupportDeskButton from './support/SupportDeskButton';
import { LifeBuoy, ChevronLeft, ChevronRight, Bell } from 'lucide-react';
import ProductNotificationsTab from './shared/ProductNotificationsTab';
import Button from './shared/Button';
import { listNeighbours } from '../utils/listNav';
import McpHotelSender from './mcpservice/McpHotelSender';

// The property's own operating currency (hotel.CURRENCY) — one list for the edit and create forms.

const formatHotelPriceLabel = (price) => {
  const amount = new Intl.NumberFormat('en-US', { style: 'currency', currency: price.currency }).format(price.unit_amount / 100);
  if (!price.recurring) return `${amount} one-time`;
  const { interval, interval_count } = price.recurring;
  return interval_count === 1 ? `${amount} / ${interval}` : `${amount} / ${interval_count} ${interval}s`;
};

function HotelForm({ mode = 'create' }) {
  const navigate = useNavigate();
  const { id, accountid } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo');
  const { isSuperAdmin, isPartner, user } = useAuth();
  const { selectedAccount, isChainAdmin, canCreateProducts } = useAccount();
  const { stripeReady } = usePartnerAccess(isSuperAdmin);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [accountName, setAccountName] = useState('');
  const [partnerName, setPartnerName] = useState('');
  const [partnerId, setPartnerId] = useState('');
  const [vaults, setVaults] = useState([]);
  const [vaultsCurrentPage, setVaultsCurrentPage] = useState(1);
  const vaultsPerPage = 5;
  const [vaultsources, setVaultSources] = useState([]);
  const [accounts, setAccounts] = useState([]);
  // ?tab=<name> so a screen can send the user BACK to where they came from. Editing a full template
  // navigates away to its own page, and returning to /hotel/:id landed on Vaults — the tab the work was
  // started from was three clicks away again. Validated against the real tab names: an unknown value would
  // otherwise render a page with every tab inactive and no panel at all.
  const TAB_NAMES = ['products', 'basic', 'templates', 'transactions', 'notifications', 'support'];
  // Preferences (the hotel logo) was folded into Templates; old links follow it there.
  const MOVED_TABS = { preferences: 'templates' };
  // Tabs with a second level: ?tab=<parent>&sub=<key>. A sub-tab marked superAdmin is the only one hidden
  // from everyone else, so its parent stays reachable and simply opens on its first visible sub-tab.
  const SUBTABS = {
    // Same order as the products in the sidebar. Pipelines is superadmin-only there too.
    products: [
      { key: 'vaults', label: 'Vaults' },
      { key: 'apps', label: 'Apps' },
      { key: 'sync', label: 'Sync' },
      { key: 'pipelines', label: 'Pipelines', superAdmin: true },
      { key: 'universalapi', label: 'Universal API' },
      // MCP as a Service — the hotel's own guest email sender, overriding the chain's (Chain Settings › MCP).
      // Superadmin only for now (HIL-140).
      { key: 'mcp', label: 'MCP', superAdmin: true },
      { key: 'credentials', label: 'Credentials' },
      { key: 'pricing', label: 'Pricing', superAdmin: true },
    ],
    basic: [
      { key: 'general', label: 'General', superAdmin: true },
      { key: 'address', label: 'Address' },
      { key: 'contact', label: 'Contact Information' },
      { key: 'billing', label: 'Billing' },
      // V663: the named prices its billing documents use (not Products › Pricing, the vault price overrides).
      // PARTNER-ONLY (teal marker): the partner's deal with the hotel; the lambda enforces the same.
      { key: 'prices', label: 'Billing prices', partnerOnly: true },
    ],
  };
  const visibleSubTabs = (tab) => (SUBTABS[tab] || [])
    .filter((t) => (!t.superAdmin || isSuperAdmin) && (!t.partnerOnly || isPartner || isSuperAdmin));
  // These used to be top-level tabs, so an old ?tab=vaults / ?tab=address link still lands on its panel.
  const parentOf = (key) => Object.keys(SUBTABS).find((tab) => visibleSubTabs(tab).some((t) => t.key === key));
  const requestedTab = searchParams.get('tab');
  const requestedSub = searchParams.get('sub');
  const legacyParent = parentOf(requestedTab);
  const [activeTab, setActiveTab] = useState(
    legacyParent || MOVED_TABS[requestedTab] || (TAB_NAMES.includes(requestedTab) ? requestedTab : 'products')
  );
  // The sub-tab last shown under each parent, so switching away and back returns to it.
  const [subTabs, setSubTabs] = useState(() => {
    const initial = Object.fromEntries(Object.keys(SUBTABS).map((tab) => [tab, visibleSubTabs(tab)[0]?.key]));
    const parent = legacyParent || requestedTab;
    const sub = legacyParent ? requestedTab : requestedSub;
    if (SUBTABS[parent] && visibleSubTabs(parent).some((t) => t.key === sub)) initial[parent] = sub;
    return initial;
  });
  const subTab = subTabs[activeTab];
  // Write the tab back to ?tab= so it can be linked to and survives a refresh. REPLACE, not push, or every
  // tab click becomes a Back press; the other params (returnTo) are carried through untouched.
  const writeTabParams = (tab, sub) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set('tab', tab);
      if (sub) params.set('sub', sub); else params.delete('sub');
      return params;
    }, { replace: true });
  };
  const changeTab = (next) => {
    setActiveTab(next);
    writeTabParams(next, subTabs[next]);
  };
  const changeSubTab = (next) => {
    setSubTabs((prev) => ({ ...prev, [activeTab]: next }));
    writeTabParams(activeTab, next);
  };
  const [logoUploading, setLogoUploading] = useState(false);
  // Create only: who pays, and the hotel's own billing details when it pays for itself. The lambda refuses
  // a hotel whose payer has no billing details; billingProblem says so before the request is sent.
  const [billing, setBilling] = useState({ billTo: 'HOTEL', billToCredentialId: null, details: {} });
  const [billingProblem, setBillingProblem] = useState(null);
  // The Billing tab on the edit page: saved by Update Hotel, like every other tab here (BillingTab `mode`).
  const billingRef = useRef(null);
  // The Billing prices tab, saved by Update Hotel the same way (HotelPrices).
  const pricesRef = useRef(null);
  const [prefError, setPrefError] = useState(null);
  const [formData, setFormData] = useState({
    accountid: '',
    hotelname: '',
    hotelcode: '',
    ADDRESS1: '',
    ADDRESS2: '',
    CITY: '',
    STATE: '',
    ZIPCODE: '',
    PHONE: '',
    FAX: '',
    WEBSITE: '',
    CONTACTNAME: '',
    CONTACTPHONE: '',
    CONTACTMOBILE: '',
    CONTACTEMAIL: '',
    COUNTRY: '',
    timezone: '',
    CURRENCY: 'USD',
    ALLOWYN: 'Y',
    INACTIVEDATE: null,
    BILLINGCONTACT: '',
    BILLINGEMAIL: '',
    BILLINGPHONE: '',
    billingmethod: '',
    BILLINGADDRESS1: '',
    BILLINGADDRESS2: '',
    BILLINGADDRESS3: '',
    BILLINGADDRESS4: '',
    BILLINGCOUNTRY: '',
    vatno: '',
    discount: '',
    stripeid: ''
  });

  const isViewMode = mode === 'view';
  const isEditMode = mode === 'edit';
  const isCreateMode = mode === 'create';

  // Hotel override pricing
  const [allStripeProducts, setAllStripeProducts] = useState([]);
  const [productConfigs, setProductConfigs] = useState({});
  const [hotelProductsLoading, setHotelProductsLoading] = useState(false);
  const [hotelProductsMsg, setHotelProductsMsg] = useState(null);
  const [hotelProductsSaving, setHotelProductsSaving] = useState(false);
  const [hotelPriceModal, setHotelPriceModal] = useState(null);
  const [creatingProductKey, setCreatingProductKey] = useState(null);
  const [archivingPriceId, setArchivingPriceId] = useState(null);
  const [platformProducts, setPlatformProducts] = useState([]);
  const [partnerSellsProducts, setPartnerSellsProducts] = useState(new Set());
  const [hotelSeats, setHotelSeats] = useState({});
  const [hotelUsedSeats, setHotelUsedSeats] = useState({});
  const [hotelSeatsSaving, setHotelSeatsSaving] = useState(false);

  useEffect(() => {
    // Auto-populate accountid if creating from account page
    if (accountid && isCreateMode) {
      console.log('Setting accountid from params:', accountid);
      setFormData(prev => ({ ...prev, accountid: parseInt(accountid) }));
      fetchAccount(accountid);
    }
  }, [accountid, isCreateMode]);

  useEffect(() => {
    // Fetch account when formData.accountid is set (edit/view mode)
    if (formData.accountid && (isEditMode || isViewMode)) {
      fetchAccount(formData.accountid);
    }
  }, [formData.accountid, isEditMode, isViewMode]);

  useEffect(() => {
    if ((isEditMode || isViewMode) && id) {
      fetchHotel();
      fetchVaults();
      fetchVaultSources();
    }
  }, [id, isEditMode, isViewMode]);

  useEffect(() => {
    if (id && activeTab === 'products' && subTab === 'pricing') {
      loadHotelProducts();
    }
  }, [id, activeTab, subTab, partnerId]);

  const HOTEL_SLOT_DEFS = {
    upfront: { ext: `h-${id}-1yr-upfront`,    interval: null,   interval_count: null },
    annual:  { ext: `h-${id}-1yr-reoccuring`, interval: 'year', interval_count: 1 },
    monthly: { ext: `h-${id}-1mo-reoccuring`, interval: 'month', interval_count: 1 },
  };

  const flashHotelProductsMsg = (text, type = 'success') => {
    setHotelProductsMsg({ type, text });
    setTimeout(() => setHotelProductsMsg(null), 4000);
  };

  const loadHotelProducts = async () => {
    setHotelProductsLoading(true);
    setHotelProductsMsg(null);
    try {
      const platformRes = await productsService.list();
      const products = platformRes.data?.products || [];
      setPlatformProducts(products);

      const calls = [
        pricingService.getHotelProductSeats(id),
        ...products.map(p => pricingService.getConfig(p.name)),
      ];
      if (products.length) calls.unshift(pricingService.listProducts(products[0].name));
      if (partnerId) calls.push(pricingService.getPartnerPlatformProducts(partnerId));

      const results = await Promise.all(calls);
      let offset = 0;

      if (products.length) {
        setAllStripeProducts(results[offset].data?.products || []);
        offset++;
      }

      const seatsResult = results[offset++];
      const seatsMap = {};
      const usedMap = {};
      (seatsResult.data?.seats || []).forEach(s => {
        seatsMap[s.product_id] = s.free_seats;
        usedMap[s.product_id] = s.used_seats ?? 0;
      });
      setHotelSeats(seatsMap);
      setHotelUsedSeats(usedMap);

      const configs = {};
      products.forEach((p, i) => {
        configs[p.name] = results[offset + i].data?.config || { price_prefix: '' };
      });
      setProductConfigs(configs);
      offset += products.length;

      if (partnerId) {
        const partnerProducts = results[offset].data?.productIds || [];
        setPartnerSellsProducts(new Set(
          products.filter(p => partnerProducts.includes(p.id)).map(p => p.name)
        ));
      }
    } catch (err) {
      setHotelProductsMsg({ type: 'error', text: 'Failed to load products' });
    } finally {
      setHotelProductsLoading(false);
    }
  };

  const getSlotProduct = (productType, slotKey) => {
    const prefix = productConfigs[productType]?.price_prefix;
    if (!prefix) return null;
    const name = `${prefix}${HOTEL_SLOT_DEFS[slotKey].ext}`;
    return allStripeProducts.find(p => p.name === name) || null;
  };

  const createHotelSlotProduct = async (productType, slotKey) => {
    const prefix = productConfigs[productType]?.price_prefix;
    const name = `${prefix}${HOTEL_SLOT_DEFS[slotKey].ext}`;
    setCreatingProductKey({ productType, slotKey });
    try {
      const res = await pricingService.createProduct(productType, { name });
      setAllStripeProducts(prev => [...prev, res.data.product]);
      flashHotelProductsMsg(`Product "${name}" created in Stripe`);
    } catch (err) {
      flashHotelProductsMsg(err.response?.data?.error?.message || 'Failed to create product', 'error');
    } finally {
      setCreatingProductKey(null);
    }
  };

  const openAddHotelPrice = (productType, productId, slotKey) => {
    const slot = HOTEL_SLOT_DEFS[slotKey];
    const prefix = productConfigs[productType]?.price_prefix || '';
    setHotelPriceModal({
      productType,
      productId,
      amount: '',
      currency: 'EUR',
      interval: slot.interval,
      interval_count: slot.interval_count,
      nickname: '',
      lookup_key: `${prefix}${slot.ext}`,
    });
  };

  const saveHotelPrice = async () => {
    setHotelProductsSaving(true);
    try {
      const res = await pricingService.createPrice(hotelPriceModal.productType, {
        productId: hotelPriceModal.productId,
        amount: hotelPriceModal.amount,
        currency: hotelPriceModal.currency,
        ...(hotelPriceModal.interval ? { interval: hotelPriceModal.interval, interval_count: hotelPriceModal.interval_count } : {}),
        nickname: hotelPriceModal.nickname || undefined,
        lookup_key: hotelPriceModal.lookup_key || undefined,
      });
      const newPrice = res.data.price;
      setAllStripeProducts(prev => prev.map(p =>
        p.id === hotelPriceModal.productId
          ? { ...p, prices: [...p.prices, newPrice].sort((a, b) => (a.unit_amount || 0) - (b.unit_amount || 0)) }
          : p
      ));
      setHotelPriceModal(null);
      flashHotelProductsMsg('Price created');
    } catch (err) {
      flashHotelProductsMsg(err.response?.data?.error?.message || 'Failed to create price', 'error');
    } finally {
      setHotelProductsSaving(false);
    }
  };

  const archiveHotelPrice = async (productType, productId, priceId) => {
    if (!window.confirm('Archive this price? It will no longer be available.')) return;
    setArchivingPriceId(priceId);
    try {
      await pricingService.archivePrice(productType, priceId);
      setAllStripeProducts(prev => prev.map(p =>
        p.id === productId ? { ...p, prices: p.prices.filter(pr => pr.id !== priceId) } : p
      ));
      flashHotelProductsMsg('Price archived');
    } catch (err) {
      flashHotelProductsMsg(err.response?.data?.error?.message || 'Failed to archive price', 'error');
    } finally {
      setArchivingPriceId(null);
    }
  };

  const renderHotelProductSlot = (productType, slotKey) => {
    const prefix = productConfigs[productType]?.price_prefix;
    const product = getSlotProduct(productType, slotKey);
    const fullName = prefix
      ? `${prefix}${HOTEL_SLOT_DEFS[slotKey].ext}`
      : `{prefix}${HOTEL_SLOT_DEFS[slotKey].ext}`;
    const isCreating = creatingProductKey?.productType === productType && creatingProductKey?.slotKey === slotKey;
    return (
      <div className="hotel-product-slot">
        <div className="hotel-product-name">{fullName}</div>
        {!product ? (
          <div className="hotel-product-missing">
            <span className="hotel-product-missing-text">Not yet created in Stripe.</span>
            <button
              type="button"
              className="btn-primary hotel-btn-sm"
              onClick={() => createHotelSlotProduct(productType, slotKey)}
              disabled={!prefix || isCreating}
            >
              {isCreating ? 'Creating...' : 'Create in Stripe'}
            </button>
          </div>
        ) : (
          <div className="hotel-prices-section">
            <div className="hotel-prices-header">
              <span className="hotel-prices-label">Prices</span>
              <button type="button" className="hotel-btn-add-price" onClick={() => openAddHotelPrice(productType, product.id, slotKey)}>
                + Add Price
              </button>
            </div>
            {product.prices.length === 0 ? (
              <div className="hotel-no-prices">No prices configured.</div>
            ) : (
              <table className="hotel-prices-table">
                <thead>
                  <tr><th>Amount</th><th>Billing</th><th>Nickname</th><th>Lookup Key</th><th></th></tr>
                </thead>
                <tbody>
                  {product.prices.map(price => (
                    <tr key={price.id} className={archivingPriceId === price.id ? 'hotel-price-row-archiving' : ''}>
                      <td className="hpt-amount">{formatHotelPriceLabel(price)}</td>
                      <td className="hpt-interval">{price.recurring ? (price.recurring.interval_count === 1 ? `/ ${price.recurring.interval}` : `/ ${price.recurring.interval_count} ${price.recurring.interval}s`) : 'one-time'}</td>
                      <td>{price.nickname || <span className="hpt-muted">—</span>}</td>
                      <td className="hpt-lookup-key">{price.lookup_key || <span className="hpt-muted">—</span>}</td>
                      <td className="hpt-actions">
                        <button
                          type="button"
                          className="hotel-btn-danger-sm"
                          onClick={() => archiveHotelPrice(productType, product.id, price.id)}
                          disabled={archivingPriceId === price.id}
                        >
                          {archivingPriceId === price.id ? 'Archiving...' : 'Archive'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderProductOverrideSection = (platformProduct) => {
    const { name, display_name } = platformProduct;
    const prefix = productConfigs[name]?.price_prefix;
    return (
      <div key={name} className="hotel-override-section" style={{ marginBottom: '2rem' }}>
        <h4 className="hotel-override-title">{display_name} Override Pricing</h4>
        <p className="hotel-override-desc">
          Hotel-specific prices for {display_name}. These override chain, partner and default pricing at checkout.
          Fallback order: hotel → chain → partner → default.
        </p>
        {!prefix && (
          <div className="hotel-prefix-notice">
            Price prefix not configured for {display_name}. Set it in Admin → Products → {display_name} → Pricing Configuration.
          </div>
        )}
        <div className="hotel-override-group">
          <div className="hotel-override-group-label">
            Fixed Price Upfront <span className="hotel-billing-badge badge-invoice">Invoice / Bank Transfer</span>
          </div>
          <p className="hotel-override-group-note">Price × retention years = invoice total.</p>
          {renderHotelProductSlot(name, 'upfront')}
        </div>
        <div className="hotel-override-group">
          <div className="hotel-override-group-label">
            Recurring Billing <span className="hotel-billing-badge badge-card">Credit Card</span>
          </div>
          <p className="hotel-override-group-note">Creates a Stripe subscription at checkout.</p>
          <div className="hotel-recurring-label">Annual</div>
          {renderHotelProductSlot(name, 'annual')}
          <div className="hotel-recurring-label hotel-recurring-label-spaced">Monthly</div>
          {renderHotelProductSlot(name, 'monthly')}
        </div>
      </div>
    );
  };

  useEffect(() => {
    // Fetch all accounts for the selector when creating a hotel without a pre-set accountid
    if (isCreateMode && !accountid) {
      accountService.getAllAccounts().then(r => setAccounts(r.accounts || [])).catch(() => {});
    }
  }, [isCreateMode, accountid]);

  useEffect(() => {
    // Pre-select hotel chain: navbar selection → user's default account → user's own account
    // Runs separately so it re-fires when selectedAccount loads from localStorage
    if (isCreateMode && !accountid) {
      const defaultAccountId = selectedAccount || user?.defaultaccountid || user?.ACCOUNTID;
      if (defaultAccountId) {
        setFormData(prev => ({ ...prev, accountid: parseInt(defaultAccountId) }));
      }
    }
  }, [isCreateMode, accountid, selectedAccount]);

  const fetchHotel = async () => {
    try {
      setLoading(true);
      const response = await hotelService.getHotelById(id, accountid);
      const hotelData = response.hotel || response;
      setFormData(hotelData);
    } catch (err) {
      setError(err.response?.data?.error?.message || err.response?.data?.message || 'Failed to fetch hotel');
    } finally {
      setLoading(false);
    }
  };


  const fetchVaults = async () => {
    try {
      const response = await vaultService.getAllVaults({ hotelid: id });
      setVaults(response.vaults || []);
    } catch (err) {
      console.error('Failed to fetch vaults:', err);
    }
  };

  const fetchVaultSources = async () => {
    try {
      const response = await vaultsourceService.getAllVaultSources();
      setVaultSources(response.vaultsources || []);
    } catch (err) {
      console.error('Failed to fetch vault sources:', err);
    }
  };

  const fetchAccount = async (accountId) => {
    try {
      const response = await accountService.getAccountById(accountId);
      const accountData = response.account || response;
      setAccountName(accountData.COMPANYNAME || '');

      if (isCreateMode) {
        const country = accountData.COUNTRY || accountData.country || '';
        const currency = accountData.CURRENCY || accountData.currency || '';
        setFormData(prev => ({
          ...prev,
          ...(country && !prev.COUNTRY ? { COUNTRY: country } : {}),
          ...(currency && prev.CURRENCY === 'USD' ? { CURRENCY: currency } : {}),
        }));
      }

      // Fetch partner if we have a partnerid
      if (accountData.partnerid) {
        fetchPartner(accountData.partnerid);
      }
    } catch (err) {
      console.error('Failed to fetch account:', err);
    }
  };

  const fetchPartner = async (partnerIdValue) => {
    try {
      const response = await partnerService.getPartnerById(partnerIdValue);
      const partnerData = response.partner || response;
      setPartnerName(partnerData.name || partnerData.NAME || '');
      setPartnerId(partnerIdValue);
    } catch (err) {
      console.error('Failed to fetch partner:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCountryChange = (e) => {
    setFormData(prev => ({
      ...prev,
      COUNTRY: e.target.value
    }));
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { setPrefError('Please select an image file'); return; }
    if (file.size > 500 * 1024) { setPrefError('Image must be under 500KB'); return; }
    setLogoUploading(true);
    setPrefError(null);
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const base64 = ev.target.result;
      try {
        await hotelService.patchHotel(id, { logo: base64, logo_content_type: file.type });
        setFormData(prev => ({ ...prev, logo: base64, logo_content_type: file.type }));
      } catch (err) {
        setPrefError('Failed to upload logo');
      } finally {
        setLogoUploading(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleLogoRemove = async () => {
    setLogoUploading(true);
    setPrefError(null);
    try {
      await hotelService.patchHotel(id, { logo: null, logo_content_type: null });
      setFormData(prev => ({ ...prev, logo: null, logo_content_type: null }));
    } catch (err) {
      setPrefError('Failed to remove logo');
    } finally {
      setLogoUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Validate required fields
    if (!formData.accountid) {
      setError('Account ID is required');
      setLoading(false);
      return;
    }

    if (!formData.hotelname) {
      setError('Hotel name is required');
      setLoading(false);
      return;
    }

    if (isCreateMode && !String(formData.hotelcode || '').trim()) {
      setError('Hotel code is required');
      setLoading(false);
      return;
    }

    if (isCreateMode && billingProblem) {
      setError(billingProblem);
      setLoading(false);
      return;
    }

    try {
      console.log('Submitting hotel data:', formData);

      if (isCreateMode) {
        const response = await hotelService.createHotel({ ...formData, billing });
        console.log('Hotel created:', response);
        if (returnTo) {
          navigate(`${returnTo}?hotelid=${response.insertId}`);
        } else {
          const targetAccountId = accountid || formData.accountid;
          if (targetAccountId) {
            navigate(`/accounts/${targetAccountId}`);
          } else {
            navigate('/partners');
          }
        }
      } else if (isEditMode) {
        // BILLING SAVES WITH THE HOTEL. Checked first, so an incomplete Billing tab stops the save before the hotel
        // half lands; saved second, like the chain's SyncConnect settings, so a billing failure shows with the hotel
        // already saved. An untouched Billing tab has nothing pending and never blocks the save.
        const billingTab = billingRef.current;
        const billingPending = billingTab && billingTab.pending();
        const pricesTab = pricesRef.current;
        const pricesPending = pricesTab && pricesTab.pending();
        const tabProblem = (billingPending && billingPending.problem) || (pricesPending && pricesPending.problem);
        if (tabProblem) {
          setError(tabProblem);
          setLoading(false);
          // The message shows at the top of the page; Update Hotel is at the bottom.
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
        const response = await hotelService.updateHotel(id, formData);
        console.log('Hotel updated:', response);
        if (billingPending) await billingTab.save();
        if (pricesPending) await pricesTab.save();
        // Navigate to hotel view screen after update
        navigate(`/hotel/${id}${keepPlace()}`);
      }
    } catch (err) {
      console.error('Error saving hotel:', err);
      setError(err.response?.data?.error?.message || err.response?.data?.message || err.message || 'Failed to save hotel');
      setLoading(false);
    }
  };

  // WHERE BACK GOES, AND WHAT IT SAYS: the screen the hotel was opened from (?returnTo=, e.g. /hotels), else its chain.
  const RETURN_LABELS = { '/hotels': 'Back to Hotels' };
  const backLabel = returnTo ? (RETURN_LABELS[returnTo.split('?')[0]] || 'Back') : 'Back to Account (Hotel Chain)';
  // SWITCHING BETWEEN VIEW AND EDIT KEEPS THE PLACE: the same tab and sub-tab, and ?returnTo= so Back still returns
  // where the user started. A billing period open on the Billing tab (?period=) is not carried — the tab opens on its
  // list of billing periods.
  const keepPlace = () => {
    const q = new URLSearchParams(searchParams);
    q.delete('period');
    const text = q.toString();
    return text ? `?${text}` : '';
  };

  const handleCancel = () => {
    if (returnTo) {
      navigate(returnTo);
      return;
    }
    const targetAccountId = accountid || formData.accountid;
    if (targetAccountId) {
      navigate(`/accounts/${targetAccountId}`);
    } else {
      navigate('/partners');
    }
  };

  if (loading && (isEditMode || isViewMode)) {
    return <div className="hotel-form-container">Loading hotel...</div>;
  }

  if (isCreateMode && !accountid && !selectedAccount) {
    return (
      <div className="hotel-form-container">
        <div className="error-message">Select a hotel chain from the navbar before creating a hotel.</div>
      </div>
    );
  }

  if (isCreateMode && !stripeReady) {
    return (
      <div className="hotel-form-container">
        <div className="error-message">Your partner account must have Stripe connected before you can create hotels.</div>
      </div>
    );
  }

  // PREVIOUS / NEXT through the Hotels list this hotel was opened from (its order as filtered there — utils/listNav).
  // View mode only: on the edit page it would throw away unsaved changes. Keeps the tab and Back to Hotels.
  const neighbours = isViewMode && id && returnTo === '/hotels' ? listNeighbours('hotels', id) : null;

  return (
    <div className="hotel-form-container">
      {neighbours && (neighbours.prev || neighbours.next) && (
        <div className="hotel-list-nav">
          <Button variant="secondary" disabled={!neighbours.prev}
            onClick={() => neighbours.prev && navigate(`/hotel/${neighbours.prev.id}${keepPlace()}`)}
            title={neighbours.prev ? `Previous: ${neighbours.prev.label}` : 'This is the first hotel in the list'}>
            <ChevronLeft className="icon" /> Previous
          </Button>
          <span className="hotel-list-nav-position">{neighbours.index + 1} of {neighbours.total}</span>
          <Button variant="secondary" disabled={!neighbours.next}
            onClick={() => neighbours.next && navigate(`/hotel/${neighbours.next.id}${keepPlace()}`)}
            title={neighbours.next ? `Next: ${neighbours.next.label}` : 'This is the last hotel in the list'}>
            Next <ChevronRight className="icon" />
          </Button>
        </div>
      )}
      {/* Breadcrumb */}
      {(partnerName || accountName || formData.hotelname) && (isViewMode || isEditMode) && (
        <div className="breadcrumb">
          {partnerName && partnerId && (
            <>
              <Link to={`/partners/${partnerId}`} className="breadcrumb-link">
                {partnerName}
              </Link>
              <span className="breadcrumb-separator">/</span>
            </>
          )}
          {accountName && formData.accountid && (
            <>
              <Link to={`/accounts/${formData.accountid}`} className="breadcrumb-link">
                {accountName}
              </Link>
              <span className="breadcrumb-separator">/</span>
            </>
          )}
          <span className="breadcrumb-current">
            {formData.hotelname || 'Hotel'}
          </span>
          {/* The hotel as a component on the support desk, once its chain is there (support/SupportDeskButton). */}
          {isViewMode && id && (
            <span className="breadcrumb-actions">
              <SupportDeskButton entity="hotel" id={id} />
            </span>
          )}
        </div>
      )}

      {error && <div className="error-message">{error}</div>}

      <form onSubmit={handleSubmit} noValidate>
        {/* Summary Information - Always visible */}
        <div className="form-section">
          <div className="form-group">
            <label htmlFor="hotelname">Hotel Name *</label>
            <input
              type="text"
              id="hotelname"
              name="hotelname"
              value={formData.hotelname}
              onChange={handleChange}
              disabled={isViewMode}
            />
          </div>

          <div className="form-group">
            <label htmlFor="hotelcode">Hotel Code{isCreateMode ? ' *' : ''}</label>
            <input
              type="text"
              id="hotelcode"
              name="hotelcode"
              value={formData.hotelcode}
              onChange={handleChange}
              disabled={isViewMode}
            />
          </div>

          <div className="form-group">
            <label htmlFor="chain_hotel_number">Hotel Number</label>
            {/* Auto-assigned per chain (001, 002, …); never editable. */}
            <input
              type="text"
              id="chain_hotel_number"
              name="chain_hotel_number"
              value={formData.chain_hotel_number || ''}
              readOnly
              disabled
            />
          </div>
        </div>

        {/* Tabs Section - Only show in view/edit mode */}
        {(isViewMode || isEditMode) && id && (
          <div className="hotel-tabs-section">
            <div className="hotel-tabs">
              <button
                type="button"
                className={`tab-button ${activeTab === 'products' ? 'active' : ''}`}
                onClick={() => changeTab('products')}
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M3 5h14M3 10h14M3 15h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                <span className="tab-label">Products</span>
              </button>

              <button
                type="button"
                className={`tab-button ${activeTab === 'basic' ? 'active' : ''}`}
                onClick={() => changeTab('basic')}
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M4 4h12v12H4V4zm2 2v8h8V6H6z" fill="currentColor"/>
                  <circle cx="10" cy="10" r="1" fill="currentColor"/>
                </svg>
                <span className="tab-label">Details</span>
              </button>

              <button
                type="button"
                className={`tab-button ${activeTab === 'templates' ? 'active' : ''}`}
                onClick={() => changeTab('templates')}
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <rect x="3" y="3" width="14" height="14" rx="2" stroke="currentColor" strokeWidth="1.5"/>
                  <path d="M6 7h8M6 10h8M6 13h5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                <span className="tab-label">Templates</span>
              </button>

              <button
                type="button"
                className={`tab-button ${activeTab === 'transactions' ? 'active' : ''}`}
                onClick={() => changeTab('transactions')}
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M3 5h14M3 10h14M3 15h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  <circle cx="15" cy="15" r="3" stroke="currentColor" strokeWidth="1.5"/>
                  <path d="M15 13.5v1.5l1 1" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                </svg>
                <span className="tab-label">Transactions</span>
              </button>

              {id && (
                <button
                  type="button"
                  className={`tab-button ${activeTab === 'notifications' ? 'active' : ''}`}
                  onClick={() => changeTab('notifications')}
                >
                  <Bell size={20} />
                  <span className="tab-label">Notifications</span>
                </button>
              )}

              {id && (
                <button
                  type="button"
                  className={`tab-button ${activeTab === 'support' ? 'active' : ''}`}
                  onClick={() => changeTab('support')}
                >
                  <LifeBuoy size={20} />
                  <span className="tab-label">Support</span>
                </button>
              )}
            </div>

            <div className="tab-content-area">
              {SUBTABS[activeTab] && (
                <SubTabs tabs={visibleSubTabs(activeTab)} active={subTab} onChange={changeSubTab} />
              )}

              {activeTab === 'products' && subTab === 'vaults' && (
                <div className="tab-panel">
                  {/* Buying a vault is an admin act ('A' on the property, its chain or its partner);
                      the lambda enforces the same rule. */}
                  {canCreateProducts && (
                    <div className="vaults-tab-header">
                      <Link to={`/hotel/${id}/vault/new`} className="btn-add-vault">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                        Add Vault
                      </Link>
                    </div>
                  )}

                  <div className="table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Vault Name</th>
                          <th>Vault System</th>
                          <th>Status</th>
                          <th>Active</th>
                          <th>Created</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {vaults.length === 0 ? (
                          <tr><td colSpan="7" className="no-data">
                            <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                              <rect x="6" y="6" width="36" height="36" rx="2" stroke="#d1d5db" strokeWidth="2"/>
                              <rect x="12" y="12" width="24" height="24" rx="1" stroke="#d1d5db" strokeWidth="2"/>
                            </svg>
                            <p>No vaults found for this hotel</p>
                            <p className="no-data-hint">Create your first vault to get started</p>
                          </td></tr>
                        ) : (
                          vaults
                            .slice(
                              (vaultsCurrentPage - 1) * vaultsPerPage,
                              vaultsCurrentPage * vaultsPerPage
                            )
                            .map((vault) => {
                              const getStatusBadgeClass = (status) => {
                                switch (status) {
                                  case 'completed':
                                    return 'status-badge status-active';
                                  case 'initiated':
                                    return 'status-badge status-initiated';
                                  case 'client_executed':
                                    return 'status-badge status-syncing';
                                  case 'error':
                                    return 'status-badge status-error';
                                  default:
                                    return 'status-badge';
                                }
                              };

                              const vaultSourceName = vault.vaultsourceid
                                ? vaultsources.find(vs => vs.id === vault.vaultsourceid)?.name
                                : null;

                              const formatCreatedDate = (dateString) => {
                                if (!dateString) return '-';
                                const date = new Date(dateString);
                                const now = new Date();
                                const diffMs = now - date;
                                const diffMins = Math.floor(diffMs / 60000);
                                const diffHours = Math.floor(diffMs / 3600000);
                                const diffDays = Math.floor(diffMs / 86400000);

                                if (diffMins < 60) {
                                  return `${diffMins} min ago`;
                                } else if (diffHours < 24) {
                                  return `${diffHours}h ago`;
                                } else if (diffDays === 1) {
                                  return 'Yesterday';
                                } else if (diffDays < 7) {
                                  return `${diffDays}d ago`;
                                } else {
                                  return date.toLocaleDateString();
                                }
                              };

                              return (
                                <tr
                                  key={vault.id}
                                  className="clickable-row"
                                  onClick={() => navigate(`/vault/${vault.id}`)}
                                  title="Click to view"
                                >
                                  <td><span className="id-badge">{vault.id}</span></td>
                                  <td className="primary-text">{vault.name || 'Unnamed Vault'}</td>
                                  <td>{vaultSourceName || '-'}</td>
                                  <td>
                                    <span className={getStatusBadgeClass(vault.status)}>
                                      {vault.status || 'unknown'}
                                    </span>
                                  </td>
                                  <td>
                                    <span className={`status-badge status-${vault.active === 'Y' ? 'active' : 'inactive'}`}>
                                      {vault.active === 'Y' ? 'Active' : 'Inactive'}
                                    </span>
                                  </td>
                                  <td>{formatCreatedDate(vault.created_at)}</td>
                                  <td className="actions-cell">
                                    <button
                                      className="btn-action btn-edit-action"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        navigate(`/vault/${vault.id}/edit`);
                                      }}
                                      title="Edit"
                                    >
                                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                        <path d="M11.5 2.5l2 2L6 12H4v-2l7.5-7.5z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                        <path d="M9.5 4.5l2 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                      </svg>
                                    </button>
                                  </td>
                                </tr>
                              );
                            })
                        )}
                      </tbody>
                    </table>
                  </div>

                  {vaults.length > vaultsPerPage && (
                    <div className="pagination">
                      <button
                        onClick={() => setVaultsCurrentPage((prev) => Math.max(1, prev - 1))}
                        disabled={vaultsCurrentPage === 1}
                      >
                        Previous
                      </button>
                      <span>
                        Page {vaultsCurrentPage} of {Math.ceil(vaults.length / vaultsPerPage)}
                      </span>
                      <button
                        onClick={() =>
                          setVaultsCurrentPage((prev) =>
                            Math.min(Math.ceil(vaults.length / vaultsPerPage), prev + 1)
                          )
                        }
                        disabled={vaultsCurrentPage >= Math.ceil(vaults.length / vaultsPerPage)}
                      >
                        Next
                      </button>
                    </div>
                  )}
                </div>
              )}


              {isSuperAdmin && activeTab === 'basic' && subTab === 'general' && (
                <div className="tab-panel">
                  <div className="form-section">
                    <div className="form-group">
                      <label htmlFor="COUNTRY">Country</label>
                      <CountrySelect
                        name="COUNTRY"
                        value={formData.COUNTRY}
                        onChange={handleCountryChange}
                        disabled={isViewMode}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="CURRENCY">Currency</label>
                      <CurrencySelect
                        id="CURRENCY"
                        name="CURRENCY"
                        value={formData.CURRENCY}
                        onChange={handleChange}
                        disabled={isViewMode}
                        placeholder="— Select currency —"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="ALLOWYN">Active Status</label>
                      <select
                        id="ALLOWYN"
                        name="ALLOWYN"
                        value={formData.ALLOWYN}
                        onChange={handleChange}
                        disabled={isViewMode}
                      >
                        <option value="Y">Active</option>
                        <option value="N">Inactive</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'basic' && subTab === 'address' && (
                <div className="tab-panel">
                  <div className="form-section">
                    <div className="form-group">
                      <label htmlFor="ADDRESS1">Address Line 1</label>
                      <input type="text" id="ADDRESS1" name="ADDRESS1" value={formData.ADDRESS1 || ''} onChange={handleChange} disabled={isViewMode} />
                    </div>
                    <div className="form-group">
                      <label htmlFor="ADDRESS2">Address Line 2</label>
                      <input type="text" id="ADDRESS2" name="ADDRESS2" value={formData.ADDRESS2 || ''} onChange={handleChange} disabled={isViewMode} />
                    </div>
                    <div className="form-group">
                      <label htmlFor="CITY">City</label>
                      <input type="text" id="CITY" name="CITY" value={formData.CITY || ''} onChange={handleChange} disabled={isViewMode} />
                    </div>
                    <div className="form-group">
                      <label htmlFor="STATE">State / Province</label>
                      <input type="text" id="STATE" name="STATE" value={formData.STATE || ''} onChange={handleChange} disabled={isViewMode} />
                    </div>
                    <div className="form-group">
                      <label htmlFor="ZIPCODE">Zip / Postal Code</label>
                      <input type="text" id="ZIPCODE" name="ZIPCODE" value={formData.ZIPCODE || ''} onChange={handleChange} disabled={isViewMode} />
                    </div>
                    <div className="form-group">
                      <label htmlFor="PHONE">Phone</label>
                      <input type="tel" id="PHONE" name="PHONE" value={formData.PHONE || ''} onChange={handleChange} disabled={isViewMode} />
                    </div>
                    <div className="form-group">
                      <label htmlFor="FAX">Fax</label>
                      <input type="tel" id="FAX" name="FAX" value={formData.FAX || ''} onChange={handleChange} disabled={isViewMode} />
                    </div>
                    <div className="form-group">
                      <label htmlFor="WEBSITE">Website</label>
                      <input type="text" id="WEBSITE" name="WEBSITE" value={formData.WEBSITE || ''} onChange={handleChange} disabled={isViewMode} placeholder="e.g., www.hotel.com" />
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'basic' && subTab === 'contact' && (
                <div className="tab-panel">
                  <div className="form-section">
                    <div className="form-group">
                      <label htmlFor="CONTACTNAME">Contact Name</label>
                      <input
                        type="text"
                        id="CONTACTNAME"
                        name="CONTACTNAME"
                        value={formData.CONTACTNAME}
                        onChange={handleChange}
                        disabled={isViewMode}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="CONTACTPHONE">Contact Phone</label>
                      <input
                        type="tel"
                        id="CONTACTPHONE"
                        name="CONTACTPHONE"
                        value={formData.CONTACTPHONE}
                        onChange={handleChange}
                        disabled={isViewMode}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="CONTACTMOBILE">Contact Mobile</label>
                      <input
                        type="tel"
                        id="CONTACTMOBILE"
                        name="CONTACTMOBILE"
                        value={formData.CONTACTMOBILE}
                        onChange={handleChange}
                        disabled={isViewMode}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="CONTACTEMAIL">Contact Email</label>
                      <input
                        type="email"
                        id="CONTACTEMAIL"
                        name="CONTACTEMAIL"
                        value={formData.CONTACTEMAIL}
                        onChange={handleChange}
                        disabled={isViewMode}
                      />
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'products' && subTab === 'apps' && (
                <div className="tab-panel">
                  <AppsPage embedded />
                </div>
              )}

              {activeTab === 'products' && subTab === 'sync' && (
                <div className="tab-panel">
                  <SyncPage hotelId={parseInt(id, 10)} embedded />
                </div>
              )}

              {/* The pipelines that run here: templates with an implementation at this hotel. */}
              {isSuperAdmin && activeTab === 'products' && subTab === 'pipelines' && (
                <div className="tab-panel">
                  <IntegrateSystemsPage hotelId={parseInt(id, 10)} embedded />
                </div>
              )}

              {activeTab === 'products' && subTab === 'universalapi' && (
                <div className="tab-panel">
                  <UapiAccessTab entityType="hotel" entityId={id} accountId={formData.accountid} />
                </div>
              )}

              {isSuperAdmin && id && activeTab === 'products' && subTab === 'mcp' && (
                <div className="tab-panel">
                  <McpHotelSender hotelId={id} />
                </div>
              )}

              {activeTab === 'products' && subTab === 'credentials' && (
                <div className="tab-panel">
                  <CredentialsTab entityType="hotel" entityId={id} />
                </div>
              )}

              {activeTab === 'templates' && (
                <div className="tab-panel">
                  {/* The logo was its own Preferences tab; it lives here because the templates are what use it. */}
                  <div style={{ maxWidth: 600, marginBottom: '1.5rem', paddingBottom: '1.5rem', borderBottom: '1px solid #e5e7eb' }}>
                    <h3 style={{ margin: '0 0 0.25rem', fontSize: '1rem', fontWeight: 600, color: '#111827' }}>Hotel Logo</h3>
                    <p style={{ margin: '0 0 1rem', fontSize: '0.85rem', color: '#6b7280' }}>
                      Upload a logo for this hotel. Used in vault document templates. Max 500KB, PNG/JPG/SVG.
                    </p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <label style={{ display: 'inline-flex', alignItems: 'center', padding: '0.5rem 1rem', background: '#4a6cf7', color: '#fff', borderRadius: 7, fontSize: '0.875rem', fontWeight: 600, cursor: logoUploading ? 'not-allowed' : 'pointer', opacity: logoUploading ? 0.6 : 1 }}>
                        {logoUploading ? 'Uploading…' : (formData.logo ? 'Replace' : 'Upload')}
                        <input type="file" accept="image/*" onChange={handleLogoUpload} disabled={logoUploading} style={{ display: 'none' }} />
                      </label>
                      {formData.logo && (
                        <button
                          type="button"
                          onClick={handleLogoRemove}
                          disabled={logoUploading}
                          style={{ padding: '0.5rem 1rem', background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: 7, fontSize: '0.875rem', fontWeight: 500, cursor: 'pointer' }}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                    {formData.logo && (
                      <div style={{ marginTop: '1rem', padding: '1rem', border: '1px solid #e5e7eb', borderRadius: 8, display: 'inline-block', background: '#fff' }}>
                        <img src={formData.logo} alt="Hotel logo" style={{ maxHeight: 80, maxWidth: 240, display: 'block' }} />
                      </div>
                    )}
                    {prefError && <p style={{ color: '#dc2626', fontSize: '0.85rem', marginTop: '0.75rem' }}>{prefError}</p>}
                  </div>
                  <HotelTemplates hotelId={id} />
                </div>
              )}

              {activeTab === 'transactions' && (
                <div className="tab-panel">
                  <OhipTransactionsTab entityType="hotel" entityId={id} />
                </div>
              )}

              {/* Support tickets raised about this hotel (shared list — support/SupportTicketsPanel). */}
              {id && activeTab === 'notifications' && (
                <div className="tab-panel">
                  <ProductNotificationsTab level="hotel" id={id} />
                </div>
              )}

              {id && activeTab === 'support' && (
                <div className="tab-panel">
                  <div className="support-panel-actions">
                    <SupportButton context={{ product: 'Hub', screen: 'hotel', hotelId: id }} label="New ticket" variant="primary" />
                  </div>
                  <SupportTicketsPanel scope="hotel" hotelId={id} emptyText="No support tickets for this hotel yet" />
                </div>
              )}

              {isSuperAdmin && activeTab === 'products' && subTab === 'pricing' && (
                <div className="tab-panel">

                  {/* ── Free seats per product ── */}
                  {platformProducts.filter(p => partnerSellsProducts.size === 0 || partnerSellsProducts.has(p.name)).map(({ id: productId, name, display_name }) => {
                    const seats = hotelSeats[productId] ?? 0;
                    const used = hotelUsedSeats[productId] ?? 0;
                    const total = seats + used;
                    return (
                      <div key={name} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem', padding: '0.75rem 1rem', background: '#f9fafb', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
                        <label style={{ fontSize: '0.875rem', fontWeight: 600, color: '#374151', whiteSpace: 'nowrap' }}>
                          {display_name} free seats
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={seats}
                          onChange={e => setHotelSeats(prev => ({ ...prev, [productId]: parseInt(e.target.value) || 0 }))}
                          style={{ width: '80px', padding: '0.3rem 0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.875rem' }}
                        />
                        <span style={{ fontSize: '0.8125rem', color: '#6b7280' }}>free {display_name} projects for this hotel</span>
                        {total > 0 && (
                          <span style={{ fontSize: '0.8125rem', color: used > 0 ? '#dc2626' : '#6b7280', background: used > 0 ? '#fef2f2' : '#f3f4f6', border: `1px solid ${used > 0 ? '#fecaca' : '#e5e7eb'}`, borderRadius: '4px', padding: '0.15rem 0.5rem', whiteSpace: 'nowrap' }}>
                            {used} / {total} used
                          </span>
                        )}
                        <button
                          type="button"
                          className="btn-primary"
                          style={{ fontSize: '0.8rem', padding: '0.35rem 0.875rem', marginLeft: 'auto' }}
                          disabled={hotelSeatsSaving}
                          onClick={async () => {
                            setHotelSeatsSaving(true);
                            try {
                              await pricingService.saveHotelProductSeats(id, [{ productId, freeSeats: hotelSeats[productId] ?? 0 }]);
                              setHotelProductsMsg({ type: 'success', text: `Free seats saved for ${display_name}` });
                              setTimeout(() => setHotelProductsMsg(null), 3000);
                            } catch (err) {
                              setHotelProductsMsg({ type: 'error', text: `Failed to save free seats for ${display_name}` });
                            } finally {
                              setHotelSeatsSaving(false);
                            }
                          }}
                        >
                          {hotelSeatsSaving ? 'Saving...' : 'Save Seats'}
                        </button>
                      </div>
                    );
                  })}

                  {hotelProductsMsg && (
                    <div className={`hotel-products-msg hotel-products-msg-${hotelProductsMsg.type}`}>{hotelProductsMsg.text}</div>
                  )}

                  {hotelProductsLoading ? (
                    <div className="loading">Loading products...</div>
                  ) : (
                    platformProducts
                      .filter(p => partnerSellsProducts.size === 0 || partnerSellsProducts.has(p.name))
                      .map(renderProductOverrideSection)
                  )}
                </div>
              )}

              {/* Who pays, and their details — shared with chain settings, partner settings and vendors. */}
              {/* Kept mounted while editing (hidden on the other tabs), so what was typed in it survives a tab
                  switch and Update Hotel can still save it. */}
              {((activeTab === 'basic' && subTab === 'billing') || isEditMode) && (
                <div className="tab-panel" style={activeTab === 'basic' && subTab === 'billing' ? undefined : { display: 'none' }}>
                  <BillingTab ref={billingRef} entity="hotel" entityId={id} mode={isEditMode ? 'edit' : 'view'} />
                </div>
              )}
              {/* Billing prices (V663): kept mounted while editing for the same reason as Billing above. */}
              {(isPartner || isSuperAdmin) && ((activeTab === 'basic' && subTab === 'prices') || isEditMode) && (
                <div className="tab-panel" style={activeTab === 'basic' && subTab === 'prices' ? undefined : { display: 'none' }}>
                  <HotelPrices ref={pricesRef} hotelId={id} mode={isEditMode ? 'edit' : 'view'} />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Show fields inline for create mode */}
        {isCreateMode && (
          <>
            <div className="form-section">
              <h3>Details</h3>

              <div className="form-group">
                <label htmlFor="COUNTRY-create">Country</label>
                <CountrySelect
                  name="COUNTRY"
                  value={formData.COUNTRY}
                  onChange={handleCountryChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="CURRENCY-create">Currency</label>
                <CurrencySelect
                  id="CURRENCY-create"
                  name="CURRENCY"
                  value={formData.CURRENCY}
                  onChange={handleChange}
                  placeholder="— Select currency —"
                />
              </div>

              <div className="form-group">
                <label htmlFor="ALLOWYN-create">Active Status</label>
                <select
                  id="ALLOWYN-create"
                  name="ALLOWYN"
                  value={formData.ALLOWYN}
                  onChange={handleChange}
                >
                  <option value="Y">Active</option>
                  <option value="N">Inactive</option>
                </select>
              </div>
            </div>

            <div className="form-section">
              <h3>Address</h3>
              <div className="form-group">
                <label htmlFor="ADDRESS1-create">Address Line 1</label>
                <input type="text" id="ADDRESS1-create" name="ADDRESS1" value={formData.ADDRESS1 || ''} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label htmlFor="ADDRESS2-create">Address Line 2</label>
                <input type="text" id="ADDRESS2-create" name="ADDRESS2" value={formData.ADDRESS2 || ''} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label htmlFor="CITY-create">City</label>
                <input type="text" id="CITY-create" name="CITY" value={formData.CITY || ''} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label htmlFor="STATE-create">State / Province</label>
                <input type="text" id="STATE-create" name="STATE" value={formData.STATE || ''} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label htmlFor="ZIPCODE-create">Zip / Postal Code</label>
                <input type="text" id="ZIPCODE-create" name="ZIPCODE" value={formData.ZIPCODE || ''} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label htmlFor="PHONE-create">Phone</label>
                <input type="tel" id="PHONE-create" name="PHONE" value={formData.PHONE || ''} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label htmlFor="FAX-create">Fax</label>
                <input type="tel" id="FAX-create" name="FAX" value={formData.FAX || ''} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label htmlFor="WEBSITE-create">Website</label>
                <input type="text" id="WEBSITE-create" name="WEBSITE" value={formData.WEBSITE || ''} onChange={handleChange} placeholder="e.g., www.hotel.com" />
              </div>
            </div>

            <div className="form-section">
              <h3>Contact Information</h3>

              <div className="form-group">
                <label htmlFor="CONTACTNAME-create">Contact Name</label>
                <input
                  type="text"
                  id="CONTACTNAME-create"
                  name="CONTACTNAME"
                  value={formData.CONTACTNAME}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="CONTACTPHONE-create">Contact Phone</label>
                <input
                  type="tel"
                  id="CONTACTPHONE-create"
                  name="CONTACTPHONE"
                  value={formData.CONTACTPHONE}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="CONTACTMOBILE-create">Contact Mobile</label>
                <input
                  type="tel"
                  id="CONTACTMOBILE-create"
                  name="CONTACTMOBILE"
                  value={formData.CONTACTMOBILE}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="CONTACTEMAIL-create">Contact Email</label>
                <input
                  type="email"
                  id="CONTACTEMAIL-create"
                  name="CONTACTEMAIL"
                  value={formData.CONTACTEMAIL}
                  onChange={handleChange}
                />
              </div>
            </div>

            <BillingCreateSection
              level="hotel"
              accountId={formData.accountid || accountid}
              value={billing}
              onChange={setBilling}
              onProblem={setBillingProblem}
            />
          </>
        )}

        {/* Form Actions */}
        <div className="form-actions">
          {isViewMode ? (
            <>
              {(isSuperAdmin || isPartner || isChainAdmin) && (
                <button type="button" onClick={() => navigate(`/hotel/${id}/edit${keepPlace()}`)}>
                  Edit Hotel
                </button>
              )}
              {(isSuperAdmin || returnTo) && (
                <button type="button" onClick={handleCancel}>
                  {backLabel}
                </button>
              )}
            </>
          ) : (
            <>
              <button type="submit" disabled={loading}>
                {loading ? 'Saving...' : isCreateMode ? 'Create Hotel' : 'Update Hotel'}
              </button>
              {/* Editing, Cancel returns to this hotel where the user was; creating, there is no hotel yet. */}
              <button type="button" onClick={isEditMode ? () => navigate(`/hotel/${id}${keepPlace()}`) : handleCancel} disabled={loading}>
                Cancel
              </button>
            </>
          )}
        </div>
      </form>

      {hotelPriceModal && (
        <div className="hotel-modal-overlay">
          <div className="hotel-modal">
            <h3>Add Price</h3>
            <div className="hotel-form-row">
              <div className="hotel-form-group">
                <label>Amount *</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={hotelPriceModal.amount}
                  onChange={e => setHotelPriceModal(p => ({ ...p, amount: e.target.value }))}
                  placeholder="0.00"
                  autoFocus
                />
              </div>
              <div className="hotel-form-group">
                <label>Currency *</label>
                <CurrencySelect billing value={hotelPriceModal.currency} onChange={e => setHotelPriceModal(p => ({ ...p, currency: e.target.value }))} />
              </div>
            </div>
            {hotelPriceModal.lookup_key && (
              <div className="hotel-form-group">
                <label>Lookup Key</label>
                <input type="text" value={hotelPriceModal.lookup_key} readOnly className="hotel-input-readonly" />
              </div>
            )}
            <div className="hotel-form-group">
              <label>Nickname <span className="hotel-optional">(optional)</span></label>
              <input
                type="text"
                value={hotelPriceModal.nickname}
                onChange={e => setHotelPriceModal(p => ({ ...p, nickname: e.target.value }))}
                placeholder="e.g. Hotel annual plan"
              />
            </div>
            <div className="hotel-modal-actions">
              <button type="button" className="btn-secondary" onClick={() => setHotelPriceModal(null)}>Cancel</button>
              <button
                type="button"
                className="btn-primary"
                onClick={saveHotelPrice}
                disabled={hotelProductsSaving || !hotelPriceModal.amount || parseFloat(hotelPriceModal.amount) <= 0}
              >
                {hotelProductsSaving ? 'Creating...' : 'Add Price'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default HotelForm;
